<script setup>
import { ref, watch, computed } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { useRoute } from "vue-router";
import { Slide } from "vue3-burger-menu";
import HeaderSection from "@/components/layout/HeaderSection.vue";
import FooterSection from "@/components/layout/FooterSection.vue";

const route = useRoute();
const showMenu = ref(false);
const isMenuOpen = ref(false);

const { logout, isAuthenticated, isLoading, user } = useAuth0();

// Check if user has the Admin role
const isAdmin = computed(() => {
  const roles = user.value?.['https://blything.co.uk/roles'] || [];
  return roles.includes('Admin');
});

const handleLogout = () => {
  logout({ logoutParams: { returnTo: window.location.origin } });
};

// Immediate watcher handles initial state and future Auth0 auth state changes
watch(
  () => isAuthenticated.value,
  (status) => {
    showMenu.value = status;
  },
  { immediate: true }
);
</script>

<template>
  <!-- Bypasses loader IF route.meta.hideLoader is true -->
  <div v-if="isLoading && !route.meta.hideLoader" class="app-loading">
    <div class="grid-x cell small-12">
      <div class="cell medium-2 large-3"></div>
      <div class="text-wrapper cell small-12 medium-8 large-6">
        <h1 id="welcome">Loading...</h1>
      </div>
    </div>
  </div>

  <!-- Renders immediately on SnailMail page or once Auth0 finishes loading -->
  <div v-else id="app">
    <!-- Hide navigation elements on Snail Mail page if requested -->
    <Slide 
      v-if="!route.meta.hideHeaderFooter && showMenu" 
      :is-open="isMenuOpen"
      :no-overlay="true"
    >
      <img src="@/assets/images/blything-mobile-menu-logo.png" alt="Blything Logo" />
      
      <router-link id="home" to="/">
        <font-awesome-icon :icon="['fas', 'house']" /><span>Home</span>
      </router-link>

      <router-link id="profile" to="/profile">
        <font-awesome-icon :icon="['fas', 'user']" /><span>Profile</span>
      </router-link>

      <!-- Admin link added for mobile burger menu -->
      <router-link v-if="isAdmin" id="admin" to="/admin">
        <font-awesome-icon :icon="['fas', 'users-gear']" /><span>Admin</span>
      </router-link>

      <a id="logout" @click="handleLogout">
        <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" /><span>Logout</span>
      </a>
    </Slide>

    <div id="page-stretch">
      <div v-if="!route.meta.hideHeaderFooter" id="header">
        <HeaderSection />
      </div>

      <div id="page-content" class="grid-x grid-padding-x">
        <router-view />
      </div>
    </div>

    <div v-if="!route.meta.hideHeaderFooter" id="footer" class="footer">
      <FooterSection />
    </div>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/abstract/variables" as *;
@use "./assets/styles/main.scss";

.app-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;

  p {
    color: $dark-pink;
    font-size: 7cqmin;
  }
}
</style>